import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@aircall/ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ConfirmationModal } from "@/components/ConfirmationModal/ConfirmationModal";
import { useBoolean } from "@/hooks/useBoolean/useBoolean";
import { useCall } from "@/hooks/useCall/useCall";
import { useCreateEvaluation } from "@/hooks/useCreateEvaluation/useCreateEvaluation";
import { useGenerateAIAnswers } from "@/hooks/useGenerateAIAnswers/useGenerateAIAnswers";
import { EvaluationAccordionHeaderContent } from "../EvaluationAccordionHeaderContent/EvaluationAccordionHeaderContent";
import { ScoreCardLocationState } from "../EvaluationNew/EvaluationNew";
import { EvaluationCreateFormProps } from "./EvaluationCreateForm.decl";
import { initRatingScales, initSet, mergeExistingFormCategoriesWithGeneratedAIAnswers } from "./EvaluationCreateForm.utility";
import { EvaluationCreateFormFooter } from "./components/EvaluationCreateFormFooter/EvaluationCreateFormFooter";
import { EvaluationCreateFormHeader } from "./components/EvaluationCreateFormHeader/EvaluationCreateFormHeader";
import { EvaluationCreateFormBinaryQuestion, EvaluationCreateFormRatingQuestion } from "./components/EvaluationFormQuestion/EvaluationFormQuestion";
import { EvaluationCreateFormProvider } from "./contexts/EvaluationCreateForm/EvaluationCreateFormContext";
import {
  useEvaluationCreateForm,
  useTracking,
} from "./EvaluationCreateForm.hooks";

export function EvaluationCreateForm({
  scoreCard,
  revieweeUserId,
  transcriptionId,
}: EvaluationCreateFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editedAIQuestions, setEditedAIQuestions] = useState(new Set());
  const { call } = useCall();

  const { onClick: handleOnClickTrack } = useTracking();

  const {
    value: isConfirmationModalOpen,
    setFalse: closeConfirmationModal,
    setTrue: openConfirmationModal,
  } = useBoolean();

  const addEditedAIQuestion = useCallback((questionId: string) => {
    setEditedAIQuestions((prevSet) => {
      const newSet = new Set(prevSet);

      newSet.add(questionId);

      return newSet;
    });
  }, []);

  const { createEvaluation, loadingCreateEvaluation } = useCreateEvaluation();

  const {
    generatedAIAnswersMap: generatedQuestionsMap,
    loadingGeneratedAnswers,
    aiAnswersTakingLonger,
  } = useGenerateAIAnswers(call?.id ?? "", {
    scorecard: scoreCard,
    transcriptionId,
  });

  // Map answer values to custom points.
  const ratingScales = useMemo(() => initRatingScales(scoreCard), [scoreCard]);

  const form = useEvaluationCreateForm({
    scoreCard,
    revieweeUserId,
    callUuid: call?.uuid ?? "",
    onSubmit: (value) => createEvaluation({ input: value }),
  });

  useEffect(() => {
    if (generatedQuestionsMap) {
      const currentCategories = form.getFieldValue("categories");

      const mergedCategories =
        mergeExistingFormCategoriesWithGeneratedAIAnswers(
          currentCategories,
          generatedQuestionsMap
        );

      form.setFieldValue("categories", mergedCategories);
    }
  }, [form, generatedQuestionsMap]);

  // Accordion: every category starts expanded, multiple can stay open.
  const defaultOpenedItems = useMemo(
    () => initSet(form.state.values.categories) ?? [],
    [form.state.values.categories]
  );

  const cancelEvaluationHandler = () => {
    handleOnClickTrack();
    navigate("../empty", {
      state: {
        scoreCardId: undefined,
      } as ScoreCardLocationState,
    });
  };

  const hasAIAnswer = (questionId: string, questionAiEnabled: boolean) =>
    Boolean(
      questionAiEnabled &&
        !editedAIQuestions.has(questionId) &&
        generatedQuestionsMap
    );

  return (
    <>
      <EvaluationCreateFormProvider form={form} ratingScales={ratingScales}>
        <div className="flex flex-col gap-2" data-test="evaluation-section">
          <EvaluationCreateFormHeader
            title={scoreCard.name}
            onClose={openConfirmationModal}
          />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              form.handleSubmit();
            }}
          >
            <div className="flex flex-col gap-6">
              <div className="bg-card">
                <Accordion multiple defaultValue={defaultOpenedItems}>
                  {form.state.values.categories.map(
                    (category, categoryIndex) => (
                      <AccordionItem
                        key={category.categoryOrder}
                        value={category.categoryOrder}
                        data-test={`accordion-item-${category.categoryOrder}`}
                        className="border-b border-border"
                      >
                        <AccordionTrigger
                          data-test={`accordion-header-${category.categoryOrder}`}
                          className="cursor-pointer"
                        >
                          {/* We could put the remaining number of questions in the header */}
                          <EvaluationAccordionHeaderContent
                            title={category.categoryName}
                          />
                        </AccordionTrigger>
                        <AccordionContent
                          keepMounted
                          data-test={`accordion-body-${category.categoryOrder}`}
                        >
                          <div className="flex flex-col gap-10 p-4">
                            {form.state.values.categories[
                              categoryIndex
                            ].questions.map((question, questionIndex) => {
                              if (question.questionPossibleRatingAnswers) {
                                return (
                                  <EvaluationCreateFormRatingQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleRatingAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                    isAIEnabled={question.questionAiEnabled}
                                    loadingAIQuestions={loadingGeneratedAnswers}
                                    aiAnswersTakingLonger={
                                      aiAnswersTakingLonger
                                    }
                                    hasAiAnswer={hasAIAnswer(
                                      question.scoreCardQuestionId,
                                      question.questionAiEnabled
                                    )}
                                    onEditQuestion={() =>
                                      addEditedAIQuestion(
                                        question.scoreCardQuestionId
                                      )
                                    }
                                  />
                                );
                              }

                              if (question.questionPossibleBinaryAnswers) {
                                return (
                                  <EvaluationCreateFormBinaryQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleBinaryAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                    isAIEnabled={question.questionAiEnabled}
                                    loadingAIQuestions={loadingGeneratedAnswers}
                                    aiAnswersTakingLonger={
                                      aiAnswersTakingLonger
                                    }
                                    hasAiAnswer={hasAIAnswer(
                                      question.scoreCardQuestionId,
                                      question.questionAiEnabled
                                    )}
                                    onEditQuestion={() =>
                                      addEditedAIQuestion(
                                        question.scoreCardQuestionId
                                      )
                                    }
                                  />
                                );
                              }

                              throw new Error("Unknown question type.");
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )
                  )}
                </Accordion>
              </div>
              <EvaluationCreateFormFooter
                loading={loadingCreateEvaluation}
                onClickCancel={openConfirmationModal}
                scoreCard={scoreCard}
              />
            </div>
          </form>
        </div>
      </EvaluationCreateFormProvider>
      <ConfirmationModal
        onHide={closeConfirmationModal}
        show={isConfirmationModalOpen}
        actions={[
          {
            label: t("evaluation.keep_editing"),
            onClick: closeConfirmationModal,
          },
          {
            variant: "destructive",
            label: t("evaluation.remove_it"),
            onClick: cancelEvaluationHandler,
          },
        ]}
        title={t("evaluation.cancel_evaluation")}
        description={t("evaluation.cancel_evaluation_description", {
          scoreCardName: scoreCard.name,
        })}
      />
    </>
  );
}
